import { useMemo } from 'react';

import type { AssignmentStudentDetailDTO } from '../models/statusReportDTOs';
import { Label, SimpleButton, DataTable, type TableColumn } from '@/src/ui';
import {
    ReportHeaderStyle,
    ReportCellStyle,
    Constants,
    utcToLocal,
    formatOptions,
} from '@/src/utils';
interface StudentStatusDatatableProps {
    data: Array<AssignmentStudentDetailDTO>;
    searchString: string;
    onToggleFreeze: (
        studentUserId: number,
        isFrozen: boolean,
        attemptExamAssignmentId?: number | null
    ) => void;
    pendingStudentIds?: Set<number>;
}

// Table for student-level assignment details
export default function StudentStatusReportDataTable({
    data,
    searchString,
    onToggleFreeze,
    pendingStudentIds,
}: StudentStatusDatatableProps) {
    // Filter rows by search string
    const filteredData = useMemo(() => {
        const s = searchString.trim().toLowerCase();
        if (!s) return data;

        return data.filter((row) => {
            const id = (row.studentUsername ?? '').toString().toLowerCase();
            const name = (row.studentName ?? '').toString().toLowerCase();
            const score = String(row.score ?? '').toLowerCase();
            return id.includes(s) || name.includes(s) || score.includes(s);
        });
    }, [data, searchString]);

    // Sort helpers
    const numSort = (a: number, b: number, dir: 'asc' | 'desc') => (dir === 'asc' ? a - b : b - a);

    const dateSort = (a: string | null, b: string | null, dir: 'asc' | 'desc') => {
        const at = a ? new Date(a).getTime() : 0;
        const bt = b ? new Date(b).getTime() : 0;
        return dir === 'asc' ? at - bt : bt - at;
    };

    // Define table columns
    const columns = useMemo<TableColumn[]>(
        () => [
            {
                dataKey: 'studentUsername',
                header: 'Student Username',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'studentName',
                header: 'Student Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
            },
            {
                dataKey: 'attemptDate',
                header: 'Last Attempt',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: dateSort,
                formatter: (row: AssignmentStudentDetailDTO) => {
                    if (!row.attemptDate) return '-';
                    return utcToLocal(row.attemptDate).toLocaleString(undefined, formatOptions);
                },
            },
            {
                dataKey: 'score',
                header: 'Score',
                align: 'right',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                sortMethod: numSort,
                formatter: (row: AssignmentStudentDetailDTO) =>
                    row.score === null || row.score === undefined ? '-' : `${row.score}%`,
            },
            {
                dataKey: 'reportingCategory',
                header: 'Reporting Category',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: AssignmentStudentDetailDTO) => {
                    const band = row.reportingCategory;
                    if (!band) return '-';
                    return <Label color={Constants.bandColors[band]}>{band}</Label>;
                },
            },
            {
                dataKey: 'action',
                header: 'Action',
                align: 'left',
                allowSort: false,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: AssignmentStudentDetailDTO) => {
                    const frozen = !!row.isTeacherFrozen;
                    const isPending = pendingStudentIds?.has(row.studentUserId) ?? false;
                    const label = isPending ? 'Loading...' : frozen ? 'Unfreeze' : 'Freeze';
                    return (
                        <SimpleButton
                            onClick={() =>
                                onToggleFreeze(
                                    row.studentUserId,
                                    !frozen,
                                    row.attemptExamAssignmentId ?? null
                                )
                            }
                            variant="contained"
                            color="primary"
                            disabled={isPending}
                            aria-label={`${label} ${row.studentName}`}
                        >
                            {label}
                        </SimpleButton>
                    );
                },
            },
        ],
        [onToggleFreeze, pendingStudentIds]
    );

    // Render table
    return (
        <DataTable
            data={filteredData}
            columns={columns}
            tableName="AssignmentStudentDetails"
            rowsPerPageInitial={50}
            enableExportCSV
            alternatingRowColor
            defaultSortColumn="studentName"
            defaultSortDirection="asc"
        />
    );
}
